<template>
  <div class="outlook-manage">
    <div v-if="loading" class="outlook-manage__loading">
      <LoadingSpinner />
    </div>

    <template v-else>
      <p class="outlook-manage__intro">
        Kies welke Outlook-agenda gebruikt wordt voor nieuwe afspraken.
      </p>

      <SelectInput
        id="outlook-calendar-select"
        label="Outlook agenda"
        :options="calendarOptions"
        v-model="selectedCalendarId"
        placeholder="Selecteer een agenda"
      />

      <div class="outlook-manage__actions">
        <Btn variant="tertiary" :loading="refreshing" @click="loadCalendars">Ververs agenda's</Btn>
        <Btn
          variant="secondary"
          :loading="saving"
          :disabled="!selectedCalendarId"
          @click="saveCalendar"
        >
          Opslaan
        </Btn>
        <Btn variant="danger" :loading="disconnecting" @click="disconnect">
          Verbinding verbreken
        </Btn>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import { useIntegrationStore } from '@/store/integrationStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import IntegrationService from '@/services/integrationService';

  export default {
    name: 'OutlookManageModal',
    components: { Btn, SelectInput, LoadingSpinner },
    props: {
      integration: {
        type: Object,
        required: true,
      },
      modalId: {
        type: [String, Number],
        default: null,
      },
      onUpdated: {
        type: Function,
        default: null,
      },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const integrationStore = useIntegrationStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(false);
      const refreshing = ref(false);
      const saving = ref(false);
      const disconnecting = ref(false);
      const selectedCalendarId = ref('');
      const calendars = ref([]);

      const calendarOptions = computed(() =>
        calendars.value.map((calendar) => ({
          value: calendar.id,
          label: calendar.summary || calendar.name || calendar.id,
        }))
      );

      const loadCalendars = async () => {
        refreshing.value = true;
        try {
          const response = await IntegrationService.listOutlookCalendars(props.integration.id);
          calendars.value = Array.isArray(response) ? response : response?.calendars || [];
        } catch (error) {
          const message =
            error?.response?.data?.error || error.message || 'Kon Outlook agenda\'s niet laden.';
          toastStore.addToast({ type: 'error', message });
        } finally {
          refreshing.value = false;
        }
      };

      const saveCalendar = async () => {
        if (!selectedCalendarId.value) return;
        const selectedCalendar = calendars.value.find((calendar) => calendar.id === selectedCalendarId.value);
        if (!selectedCalendar) return;

        saving.value = true;
        try {
          const settings = {
            ...(props.integration.settings || {}),
            calendar_id: selectedCalendar.id,
            calendar_name: selectedCalendar.summary || selectedCalendar.name || selectedCalendar.id,
          };

          await integrationStore.updateIntegration(props.integration.id, {
            name: props.integration.name,
            type: props.integration.type,
            is_active: props.integration.is_active,
            settings,
          });

          if (typeof props.onUpdated === 'function') {
            await props.onUpdated();
          }

          toastStore.addToast({ type: 'success', message: 'Outlook agenda opgeslagen.' });
        } catch (error) {
          const message =
            error?.response?.data?.error || error.message || 'Kon Outlook agenda niet opslaan.';
          toastStore.addToast({ type: 'error', message });
        } finally {
          saving.value = false;
        }
      };

      const disconnect = () => {
        const confirmId = uiStore.addModal(null, {
          title: 'Outlook-koppeling verbreken?',
          description: 'Outlook synchronisatie stopt direct voor nieuwe afspraken.',
          status: 'danger',
          submitText: 'Verbreek verbinding',
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            disconnecting.value = true;
            try {
              await integrationStore.deleteIntegration(props.integration.id);
              if (typeof props.onUpdated === 'function') {
                await props.onUpdated();
              }
              uiStore.closeModal(confirmId);
              if (props.modalId) {
                uiStore.closeModal(props.modalId);
              }
              toastStore.addToast({ type: 'success', message: 'Outlook koppeling verbroken.' });
            } catch (error) {
              uiStore.closeModal(confirmId);
              const message =
                error?.response?.data?.error || error.message || 'Kon Outlook koppeling niet verbreken.';
              toastStore.addToast({ type: 'error', message });
            } finally {
              disconnecting.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(confirmId),
        });
      };

      onMounted(async () => {
        loading.value = true;
        selectedCalendarId.value = props.integration?.settings?.calendar_id || '';
        await loadCalendars();
        loading.value = false;
      });

      return {
        loading,
        refreshing,
        saving,
        disconnecting,
        calendars,
        selectedCalendarId,
        calendarOptions,
        loadCalendars,
        saveCalendar,
        disconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .outlook-manage {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__intro {
      margin: 0;
      color: var(--slate-600);
      font-size: 0.875rem;
    }

    &__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: flex-end;
    }
  }
</style>
